import React, { useEffect, useRef, useState } from 'react';
import { hrmsMyOnboarding, hrmsToggleOnboardingTask, hrmsUploadOnboardingDoc, hrmsDownloadMyOnboardingDoc } from '../../lib/hrmsApi';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { CheckCircle2, Circle, Upload, PartyPopper, FileText, Loader2, Play, Sparkles, Download, HeartHandshake, BookOpen } from 'lucide-react';

const CATEGORY_LABEL = { profile: 'Profile', orientation: 'Orientation', documents: 'Documents', setup: 'Setup', custom: 'Extra' };

const readFileAsB64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = reader.result || '';
      const b64 = String(s).split(',')[1] || '';
      resolve({ content_b64: b64, filename: file.name, mime_type: file.type || 'application/octet-stream', size: file.size });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const OnboardingPage = () => {
  const { toast } = useToast();
  const [ob, setOb] = useState(null);
  const [busy, setBusy] = useState(null); // task id or docname currently working
  const uploadRefs = useRef({});

  const load = async () => {
    try { setOb(await hrmsMyOnboarding()); } catch (err) { setOb({ notFound: true }); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (t) => {
    setBusy(t.id);
    try {
      await hrmsToggleOnboardingTask(t.id, !t.completed);
      await load();
    } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || '' }); }
    finally { setBusy(null); }
  };

  const upload = async (docName, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast({ title: 'File too large', description: 'Max 2 MB' }); return; }
    setBusy(docName);
    try {
      const meta = await readFileAsB64(file);
      await hrmsUploadOnboardingDoc({ name: docName, ...meta });
      toast({ title: `${docName} uploaded` });
      await load();
    } catch (err) { toast({ title: 'Upload failed', description: err?.response?.data?.detail || '' }); }
    finally { setBusy(null); }
  };

  const download = async (d) => {
    try {
      const res = await hrmsDownloadMyOnboardingDoc(d.id);
      const bin = atob(res.content_b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: res.mime_type });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = res.filename;
      link.click();
    } catch (err) { toast({ title: 'Download failed' }); }
  };

  if (!ob) return <div className="text-slate-500 text-sm p-4">Loading your onboarding…</div>;
  if (ob.notFound) return <div className="text-slate-500 text-sm p-4">You don't have an onboarding record yet. If you're a new joiner, ask HR to onboard you.</div>;

  const totalTasks = ob.tasks?.length || 0;
  const doneTasks = ob.tasks?.filter((t) => t.completed).length || 0;
  const pct = ob.progress || 0;
  const groups = (ob.tasks || []).reduce((acc, t) => { (acc[t.category] = acc[t.category] || []).push(t); return acc; }, {});

  return (
    <div data-testid="onboarding-page">
      {/* Hero */}
      <div className="rounded-2xl overflow-hidden border border-slate-200 bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 text-white p-6 md:p-8" data-testid="onboarding-hero">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-orange-100"><Sparkles className="h-3.5 w-3.5" /> Onboarding kit</div>
        <h1 className="text-3xl md:text-4xl font-extrabold mt-1">Welcome to Hipro, {(ob.employee_name || '').split(' ')[0] || 'friend'}! 🎉</h1>
        <p className="text-orange-100 mt-2 text-sm max-w-2xl">Let's get you set up in the next few days. Work through this checklist, upload the required documents and don't miss the welcome video below.</p>
        <div className="mt-5 flex items-center gap-4">
          <div className="flex-1 bg-white/20 rounded-full h-3 overflow-hidden max-w-md">
            <div className="h-full bg-white transition-all duration-500" style={{ width: `${pct}%` }} data-testid="onboarding-progress-bar" />
          </div>
          <div className="font-bold text-sm">{doneTasks}/{totalTasks} · {pct}%</div>
        </div>
        {pct >= 100 && <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-sm" data-testid="onboarding-complete-badge"><PartyPopper className="h-4 w-4" /> All done — you're officially onboarded!</div>}
      </div>

      {/* Welcome video */}
      {ob.welcome_video_url && (
        <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5" data-testid="onboarding-video">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest"><Play className="h-3.5 w-3.5" /> Welcome video</div>
          <h2 className="text-lg font-bold text-slate-900 mt-1">A note from the team</h2>
          <div className="mt-3 aspect-video max-w-2xl rounded-lg overflow-hidden bg-slate-900">
            <iframe src={ob.welcome_video_url} title="Welcome" className="w-full h-full" allowFullScreen frameBorder="0"></iframe>
          </div>
        </div>
      )}

      {/* Buddy + Handbook — two quick cards */}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="onboarding-buddy-card">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest"><HeartHandshake className="h-3.5 w-3.5 text-pink-500" /> Onboarding buddy</div>
          {ob.buddy_name ? (
            <>
              <div className="mt-2 text-lg font-bold text-slate-900">{ob.buddy_name}</div>
              <div className="text-xs text-slate-500">{ob.buddy_employee_id}</div>
              <a href={`mailto:${ob.buddy_email}`} className="mt-3 inline-flex items-center rounded-lg bg-pink-500 hover:bg-pink-600 text-white text-sm font-semibold px-3 py-2" data-testid="onboarding-buddy-email">Say hi → {ob.buddy_email}</a>
              <div className="mt-2 text-xs text-slate-500">Assigned {(ob.buddy_assigned_at || '').split('T')[0] || 'recently'}. Grab a 20-min coffee this week.</div>
            </>
          ) : (
            <div className="mt-2 text-sm text-slate-500">A buddy will be assigned to you soon. Ask HR if it doesn't happen within 24 hours.</div>
          )}
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="onboarding-handbook-card">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest"><BookOpen className="h-3.5 w-3.5 text-slate-700" /> Employee handbook</div>
          <div className="mt-2 text-lg font-bold text-slate-900">Read & acknowledge</div>
          <p className="text-xs text-slate-500 mt-1">A short read on how we work, our values and where to go when you need help.</p>
          <a href="/hrms/me/handbook" className="mt-3 inline-flex items-center rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-3 py-2" data-testid="onboarding-handbook-link">Open handbook →</a>
          {ob.handbook_acknowledged_at && <div className="mt-2 text-xs text-emerald-700 font-semibold">✓ Acknowledged on {ob.handbook_acknowledged_at.split('T')[0]}</div>}
        </div>
      </div>

      {/* Tasks grouped */}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {Object.entries(groups).map(([cat, tasks]) => (
          <div key={cat} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`onboarding-group-${cat}`}>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{CATEGORY_LABEL[cat] || cat}</div>
            <div className="mt-2 space-y-2">
              {tasks.map((t) => (
                <div key={t.id} className={`rounded-lg border p-3 flex items-start gap-3 transition-colors ${t.completed ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200 hover:border-orange-300'}`} data-testid={`onboarding-task-${t.id}`}>
                  <button onClick={() => toggle(t)} disabled={busy === t.id} className="mt-0.5" title={t.completed ? 'Mark incomplete' : 'Mark complete'} data-testid={`onboarding-task-toggle-${t.id}`}>
                    {t.completed ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Circle className="h-5 w-5 text-slate-400" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold ${t.completed ? 'text-emerald-800 line-through' : 'text-slate-900'}`}>{t.title}</div>
                    {t.description && <div className="text-xs text-slate-500 mt-0.5">{t.description}</div>}
                    {t.category === 'documents' && (
                      <div className="mt-2 flex items-center gap-2">
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => upload(t.title.replace(/^Upload\s+/i, ''), e.target.files?.[0])} ref={(el) => (uploadRefs.current[t.id] = el)} className="hidden" data-testid={`onboarding-file-${t.id}`} />
                        <Button variant="outline" onClick={() => uploadRefs.current[t.id]?.click()} disabled={busy === t.title.replace(/^Upload\s+/i, '')} className="h-8 px-2 text-xs" data-testid={`onboarding-upload-${t.id}`}>
                          {busy === t.title.replace(/^Upload\s+/i, '') ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Upload className="h-3.5 w-3.5 mr-1" /> Upload</>}
                        </Button>
                        {(ob.documents || []).filter((d) => d.name.toLowerCase() === t.title.replace(/^Upload\s+/i, '').toLowerCase()).map((d) => (
                          <button key={d.id} onClick={() => download(d)} className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> {d.filename}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Uploaded docs summary */}
      {(ob.documents?.length || 0) > 0 && (
        <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5" data-testid="onboarding-documents">
          <h2 className="text-lg font-bold text-slate-900">Your uploaded documents</h2>
          <div className="mt-3 grid sm:grid-cols-2 md:grid-cols-3 gap-2">
            {ob.documents.map((d) => (
              <button key={d.id} onClick={() => download(d)} className="rounded-lg border border-slate-200 hover:border-orange-400 p-3 text-left flex items-center gap-3" data-testid={`onboarding-doc-${d.id}`}>
                <FileText className="h-5 w-5 text-orange-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{d.name}</div>
                  <div className="text-xs text-slate-500 truncate">{d.filename} · {Math.round((d.size || 0) / 1024)} KB</div>
                </div>
                <Download className="h-4 w-4 text-slate-400 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingPage;
